import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { useNoIndex } from "../lib/useNoIndex";

/**
 * AuthCallback — handles the Emergent OAuth redirect.
 *
 * Emergent's OAuth server lands the browser back at /dashboard with the
 * fragment `#session_id=<opaque>`. This component swaps that opaque ID
 * for a real session_token (stored server-side in `user_sessions` and
 * as an httpOnly cookie), then hops to /dashboard cleanly.
 *
 * Per the Emergent playbook:
 *   - Detect session_id in URL fragment with useLocation().hash (reactive)
 *   - Use useRef for the "processed" flag (StrictMode safety), not useState
 *   - Do the exchange synchronously and navigate immediately, no loading UI beat
 */
export default function AuthCallback() {
  useNoIndex();
  const location = useLocation();
  const navigate = useNavigate();
  const { exchangeGoogleSession } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    const sessionId = params.get("session_id");
    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    exchangeGoogleSession(sessionId)
      .then(() => {
        // Strip fragment before navigating away
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/dashboard", { replace: true });
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || "Sign-in failed. Please try again.");
      });
  }, [location.hash, navigate, exchangeGoogleSession]);

  return (
    <main data-testid="auth-callback" className="flex min-h-screen items-center justify-center bg-sand">
      {error ? (
        <div className="max-w-md text-center">
          <h1 className="font-serif text-3xl text-ink">Sign-in couldn't complete.</h1>
          <p className="mt-4 text-[13px] text-olive">{error}</p>
        </div>
      ) : (
        <div className="flex items-center gap-3 text-olive">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="font-serif text-3xl">Signing you in…</span>
        </div>
      )}
    </main>
  );
}
